import { pb, getApiBase } from './pb';
import { refreshAuth } from './auth';

async function authedGetJson(url: string): Promise<any> {
  // Ensure we have a valid token (or attempt silent relogin) before calling
  await refreshAuth();

  const headers: any = { 'Content-Type': 'application/json' };
  if (pb.authStore?.token) headers['Authorization'] = `Bearer ${pb.authStore.token}`;

  const doFetch = async () => {
    console.log('[api] GET', url);
    return await fetch(url, { headers });
  };

  let resp = await doFetch();

  // If auth has expired, PB will return 401/403. Clear and try one forced relogin.
  if (resp.status === 401 || resp.status === 403) {
    console.warn('[api] auth rejected, attempting relogin and retry. status=', resp.status);
    pb.authStore.clear();
    await refreshAuth();
    if (pb.authStore?.token) headers['Authorization'] = `Bearer ${pb.authStore.token}`;
    resp = await doFetch();
  }

  if (!resp.ok) {
    let body: any = null;
    try { body = await resp.json(); } catch {}
    const msg = body?.message || body?.data || body || '';
    throw new Error(`HTTP ${resp.status} for ${url}${msg ? `: ${JSON.stringify(msg)}` : ''}`);
  }

  return await resp.json();
}

export type Option = { label: string; emoji: string };
export type ActiveQuestionStep = {
  assignmentId: string;
  id: string;
  text: string;
  options: Option[];
  position: number;
  active_from?: string;
  created?: string;
};

function normalizePosition(raw: any): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortActiveQuestionSteps(steps: ActiveQuestionStep[]): ActiveQuestionStep[] {
  return steps.sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    const activeFromCmp = String(a.active_from || '').localeCompare(String(b.active_from || ''));
    if (activeFromCmp !== 0) return activeFromCmp;
    const createdCmp = String(a.created || '').localeCompare(String(b.created || ''));
    if (createdCmp !== 0) return createdCmp;
    return a.assignmentId.localeCompare(b.assignmentId);
  });
}

export async function fetchActiveQuestionSequence(deviceId: string): Promise<ActiveQuestionStep[]> {
  console.log('[api] fetchActiveQuestionSequence deviceId=', deviceId);
  const base = getApiBase();
  // Match backend active time window.
  const filterExpr = `(device.id="${deviceId}")&&active_from<=@now&&((active_to="")||active_to>@now)`;
  const filter = encodeURIComponent(filterExpr);
  // Keep server-side sort compatible even before the `position` migration is applied.
  const url = `${base}/api/collections/assignments/records?filter=${filter}&perPage=200&sort=active_from,created,id&expand=question`;
  const json = await authedGetJson(url);
  console.log('[api] active assignment items=', json?.items?.length);

  const items = (json?.items || []) as any[];
  const steps: ActiveQuestionStep[] = [];
  for (const item of items) {
    const question = item?.expand?.question;
    if (!question) continue;
    steps.push({
      assignmentId: String(item.id),
      id: String(question.id),
      text: String(question.text || ''),
      options: (question.options || []) as Option[],
      position: normalizePosition(item?.position),
      active_from: item?.active_from,
      created: item?.created,
    });
  }

  return sortActiveQuestionSteps(steps);
}

export async function fetchActiveQuestion(deviceId: string) {
  const sequence = await fetchActiveQuestionSequence(deviceId);
  const first = sequence[0];
  if (!first) return null;
  // Backward-compatible helper for older call sites.
  return { id: first.id, text: first.text, options: first.options };
}

export async function postResponse(deviceId: string, questionId: string, optionIndex: number) {
  console.log('[api] postResponse', { deviceId, questionId, optionIndex });
  await pb.collection('responses').create({
    device: deviceId,
    question: questionId,
    option_index: optionIndex,
  });
  console.log('[api] postResponse ok');
}

export type ResponseCount = {
  id: string;
  device: string;
  question: string;
  option_index: number;
  count: number;
  last_response?: string;
};

export async function fetchResponseCountsSinceLastReset(deviceId: string, questionId?: string) {
  console.log('[api] fetchResponseCountsSinceLastReset deviceId=', deviceId, 'questionId=', questionId);
  const base = getApiBase();
  const filters: string[] = [`device="${deviceId}"`];
  if (questionId) filters.push(`question="${questionId}"`);
  const filter = encodeURIComponent(filters.join('&&'));
  const url = `${base}/api/collections/response_counts_since_last_reset/records?filter=${filter}`;
  const json = await authedGetJson(url);
  console.log('[api] items=', json?.items?.length);
  const items = (json?.items || []) as any[];
  return items.map((it) => ({
    id: String(it.id),
    device: String(it.device),
    question: String(it.question),
    option_index: Number(it.option_index),
    count: Number(it.count),
    last_response: it.last_response,
  })) as ResponseCount[];
}

export async function fetchDevice(deviceId: string) {
  console.log('[api] fetchDevice deviceId=', deviceId);
  const base = getApiBase();
  const url = `${base}/api/collections/devices/records/${encodeURIComponent(deviceId)}`;
  const json = await authedGetJson(url);
  return { id: String(json?.id || deviceId), name: String(json?.name || ''), username: String(json?.username || '') };
}

export type Question = { id: string; text: string; options: Option[] };

export async function fetchQuestionsByIds(ids: string[]) {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return [] as Question[];
  console.log('[api] fetchQuestionsByIds count=', unique.length);
  const base = getApiBase();
  // PocketBase filter: id in ("a","b",...)
  const clause = `id ~ ${unique.map((id) => `"${id}"`).join(',')}`;
  const filter = encodeURIComponent(clause);
  let url = `${base}/api/collections/questions/records?perPage=${Math.max(200, unique.length)}&filter=${filter}`;
  let json = await authedGetJson(url);
  let items = (json?.items || []) as any[];
  if (items.length < unique.length) {
    // fallback to a larger unfiltered page in case filter operator support differs
    url = `${base}/api/collections/questions/records?perPage=500`;
    console.log('[api] fallback GET', url);
    json = await authedGetJson(url);
    items = (json?.items || []) as any[];
  }
  return items.map((q) => ({ id: String(q.id), text: String(q.text), options: q.options || [] })) as Question[];
}

export async function fetchAllResponseCountsSinceLastReset() {
  console.log('[api] fetchAllResponseCountsSinceLastReset');
  const base = getApiBase();
  const url = `${base}/api/collections/response_counts_since_last_reset/records?perPage=500`;
  const json = await authedGetJson(url);
  const items = (json?.items || []) as any[];
  return items.map((it) => ({
    id: String(it.id),
    device: String(it.device),
    question: String(it.question),
    option_index: Number(it.option_index),
    count: Number(it.count),
    last_response: it.last_response,
  })) as ResponseCount[];
}

export type DeviceInfo = { id: string; name: string; username?: string; email?: string };

export async function fetchDevicesByIds(ids: string[]) {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (unique.length === 0) return [] as DeviceInfo[];
  console.log('[api] fetchDevicesByIds count=', unique.length);
  const base = getApiBase();
  const clause = `id ~ ${unique.map((id) => `"${id}"`).join(',')}`;
  const filter = encodeURIComponent(clause);
  let url = `${base}/api/collections/devices/records?perPage=${Math.max(200, unique.length)}&filter=${filter}`;
  let json = await authedGetJson(url);
  let items = (json?.items || []) as any[];
  if (items.length < unique.length) {
    // fallback: fetch a wider page and map locally
    url = `${base}/api/collections/devices/records?perPage=500`;
    console.log('[api] fallback GET', url);
    json = await authedGetJson(url);
    items = (json?.items || []) as any[];
  }
  return items.map((d) => ({ id: String(d.id), name: String(d.name || ''), username: String(d.username || ''), email: String(d.email || '') })) as DeviceInfo[];
}
